import React, { useState, useEffect, useMemo } from 'react';
import {
    MapPin, Clock, CheckCircle2, XCircle, Receipt, FileText,
    Camera, Video, ChevronDown, ChevronUp, Loader2, ExternalLink,
    AlertTriangle, Car, CreditCard, Users, Target
} from 'lucide-react';
import { useToast } from './ToastContext';
import { Modal } from './Modal';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types';
import { RelazioneGiornalieraService, IncaricoService } from '../services/apiService';
import AutoAziendaleSelector from './AutoAziendaleSelector';
import NumberInput from './NumberInput';
import {
    ApprovazioniFilters, ApprovazioneFiltersValue, EMPTY_APPROVAZIONE_FILTERS,
    passesApprovazioneFilters, ritardoMinutiReportIncarico, totaleSpeseReport,
    BulkApproveModal,
} from './ApprovazioniFilters';
import { BuonoPastoAutoCard } from './BuonoPastoAutoCard';
import { dedupKmPhotos } from '../utils/reportDedup';

const calcolaOre = (inizio: string, fine: string): number => {
    if (!inizio || !fine) return 0;
    const [h1, m1] = inizio.split(':').map(Number);
    const [h2, m2] = fine.split(':').map(Number);
    // fine < inizio = intervallo oltre la mezzanotte (19:00 → 01:00 = 6h).
    let diff = (h2 * 60 + m2) - (h1 * 60 + m1);
    if (diff < 0) diff += 24 * 60;
    return Math.max(0, Math.round(diff / 6) / 10);
};

export const TabReport = ({ praticaId, onApproved, soggetti = [], riferimento }: { praticaId: number; onApproved?: () => void; soggetti?: any[]; riferimento?: string | null }) => {
    const { addToast } = useToast();
    const { user } = useAuth();

    // Il flag "carta aziendale" decide il rimborso: il collaboratore non può
    // cambiarlo sulle proprie spese. La rotta non ha guard di ruolo, quindi il
    // controllo sta qui.
    const canRettificareSpese = user?.role === UserRole.ADMIN || user?.role === UserRole.RESPONSABILE;
    const [reports, setReports] = useState<any[]>([]);
    const [kmPhotosMap, setKmPhotosMap] = useState<Record<number, any[]>>({});
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState<number | null>(null);
    const [previewFile, setPreviewFile] = useState<any | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [savingId, setSavingId] = useState<number | null>(null);
    const [annullandoId, setAnnullandoId] = useState<number | null>(null);

    // Filtri combinabili condivisi tra le viste approvazioni.
    const [filters, setFilters] = useState<ApprovazioneFiltersValue>(EMPTY_APPROVAZIONE_FILTERS);
    // Bulk approve: stato per il modale di conferma e per il loader.
    const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
    const [savingBulk, setSavingBulk] = useState(false);
    // Selezione multipla per "Approva selezionati" senza aprire il dettaglio.
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    // Buono pasto automatico: attivabile per singola relazione, default true.
    const [applicaBpByRel, setApplicaBpByRel] = useState<Record<number, boolean>>({});
    const isBpAttivo = (relId: number) =>
        applicaBpByRel[relId] !== undefined ? applicaBpByRel[relId] : true;

    useEffect(() => {
        if (!praticaId) return;
        const load = async () => {
            try {
                const [rRes, iRes] = await Promise.all([
                    RelazioneGiornalieraService.getByPratica(praticaId),
                    IncaricoService.getByPratica(praticaId),
                ]);

                const incarichi: any[] = (iRes as any).data || iRes || [];

                const mapKms: Record<number, any[]> = {};
                const mapSoggetti: Record<number, string> = {};
                // incaricoId → dataInizio pianificata, per il filtro sul ritardo d'inizio.
                const mapDataInizio: Record<number, string | null> = {};

                // Foto km da endpoint dedicato: `inc.media` può essere filtrato
                // e non sempre le contiene, ad esempio dopo l'approvazione.
                await Promise.all(incarichi.map(async (inc: any) => {
                    mapSoggetti[inc.id] = inc.soggettoNome || (soggetti.length === 1 ? soggetti[0].nome : 'Tutti / Non specificato');
                    mapDataInizio[inc.id] = inc.dataInizio || null;
                    try {
                        const kms = await IncaricoService.getKmPhotos(inc.id);
                        if (kms.length) mapKms[inc.id] = kms;
                    } catch {
                        // Fallback su inc.media se l'endpoint non risponde
                        const fallback = (inc.media || []).filter((m: any) => m.filename?.startsWith('Km_'));
                        if (fallback.length) mapKms[inc.id] = fallback;
                    }
                }));

                setKmPhotosMap(mapKms);

                const data: any[] = Array.isArray(rRes) ? rRes : (rRes as any).data || [];
                const mapped = data.map(r => ({
                    ...r,
                    soggettoNome: mapSoggetti[r.incaricoId] || 'Tutti / Non specificato',
                    incaricoDataInizio: mapDataInizio[r.incaricoId] || null,
                    oreExtraApprovate: r.oreExtraApprovate ?? r.oreExtra ?? 0,
                    // Override totale ore approvate (incarichi). null = non rettificato
                    // → in UI mostra il default (GPS + extra) e il BE usa oreGps + oreExtraApprovate.
                    oreApprovateTotali: r.oreApprovateTotali ?? null,
                    kmApprovati: r.kmApprovati ?? r.kmPercorsi ?? 0,
                    oreFatturabiliApprovate: r.oreFatturabiliApprovate ?? calcolaOre(r.orarioInizioIndagine, r.orarioFineIndagine),
                    autoAziendale: r.autoAziendale ?? false,
                    spese: (r.speseEspanso || r.spese || []).map((s: any) => ({
                        ...s,
                        importoApprovato: s.importoApprovato ?? (s.anticipatoAzienda ? 0 : s.importo),
                    })),
                }));
                mapped.sort((a, b) => {
                    const da = a.dataRelazione ? new Date(a.dataRelazione).getTime() : 0;
                    const db = b.dataRelazione ? new Date(b.dataRelazione).getTime() : 0;
                    return da - db;
                });
                setReports(mapped);
            } catch (e) {
                addToast('Errore caricamento report', 'error');
            } finally {
                setLoading(false);
            }
        };

        load();

        // Ricarica i dati (e gli URL firmati) quando l'utente torna al tab del browser
        const handleVisibility = () => { if (!document.hidden) load(); };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [praticaId, addToast]);

    const setField = (id: number, field: string, v: any) =>
        setReports(prev => prev.map(r => r.id === id ? { ...r, [field]: v } : r));

    const setSpesa = (rid: number, sid: number, v: any, campo: string = 'importoApprovato') =>
        setReports(prev => prev.map(r =>
            r.id === rid ? { ...r, spese: r.spese.map((s: any) => s.id === sid ? { ...s, [campo]: v } : s) } : r
        ));

    // Il titolare può correggere il flag "carta aziendale" prima di approvare.
    // Va riallineato anche l'importo approvato: una spesa su carta aziendale
    // ha importoApprovato 0 e, tolto il flag, resterebbe 0 pur essendo rimborsabile.
    const toggleSpesaAnticipata = (rid: number, sid: number) =>
        setReports(prev => prev.map(r =>
            r.id === rid ? { ...r, spese: r.spese.map((s: any) => {
                if (s.id !== sid) return s;
                const anticipata = !s.anticipatoAzienda;
                return {
                    ...s,
                    anticipatoAzienda: anticipata,
                    importoApprovato: anticipata
                        ? 0
                        : (Number(s.importoApprovato) > 0 ? s.importoApprovato : s.importo),
                };
            }) } : r
        ));

    const save = async (report: any) => {
        if (!window.confirm('Approvi definitivamente?')) return;
        setSavingId(report.id);
        try {
            await RelazioneGiornalieraService.updateRevisione(report.id, {
                oreExtraApprovate: Number(report.oreExtraApprovate) || 0,
                // Override totale ore approvate (incarichi): inviato solo se il titolare
                // l'ha rettificato; null/'' → il BE applica oreGps + oreExtraApprovate.
                oreApprovateTotali: (report.oreApprovateTotali === null || report.oreApprovateTotali === undefined || report.oreApprovateTotali === '')
                    ? null
                    : Number(report.oreApprovateTotali),
                oreFatturabiliApprovate: Number(report.oreFatturabiliApprovate) || 0,
                kmApprovati: Number(report.kmApprovati) || 0,
                autoAziendale: report.autoAziendale,
                applicaBuonoPasto: isBpAttivo(report.id),
                speseApprovate: report.spese.map((s: any) => ({
                    id: s.id,
                    importoApprovato: s.anticipatoAzienda ? 0 : (Number(s.importoApprovato) || 0),
                    // Necessario perché la rettifica del flag arrivi al BE.
                    anticipatoAzienda: !!s.anticipatoAzienda,
                    importoCarta: s.anticipatoAzienda ? (Number(s.importoCarta ?? s.importo) || 0) : null,
                })),
            });
            addToast('Approvato', 'success');
            // Replica in locale quanto fatto dal BE con applicaBuonoPastoAuto
            // (toglie le righe automatiche e, sopra soglia, aggiunge quella
            // nuova), così le spese sono aggiornate senza ricaricare.
            const soglia = Number(report.collaboratore?.sogliaBuonoPastoIncarichiOre) || 0;
            const importoBp = Number(report.collaboratore?.importoBuonoPastoIncarichi) || 0;
            const oreLavorate = (report.oreApprovateTotali !== null && report.oreApprovateTotali !== undefined && report.oreApprovateTotali !== '')
                ? Number(report.oreApprovateTotali) || 0
                : (Number(report.oreGps) || 0) + (Number(report.oreExtraApprovate) || 0);
            const applicaBp = isBpAttivo(report.id);
            // Un buono ogni X ore, come nel BE: floor(ore/soglia) × importo singolo.
            const numBuoni = soglia > 0 ? Math.floor((oreLavorate / soglia) + 1e-9) : 0;
            const maturato = importoBp > 0 && numBuoni >= 1;
            const totaleBp = importoBp * numBuoni;
            setReports(prev => prev.map(r => {
                if (r.id !== report.id) return r;
                const speseSenzaAuto = (r.spese || []).filter((s: any) => !s.buonoPastoAuto)
                    .map((s: any) => s.anticipatoAzienda && s.importoCarta !== undefined
                        ? { ...s, importo: Number(s.importoCarta) || 0 } : s);
                const speseNuove = (applicaBp && maturato)
                    ? [...speseSenzaAuto, {
                        id: -r.id, // id sintetico, sostituito dal valore reale al prossimo reload
                        tipo: numBuoni > 1 ? `Buono pasto ×${numBuoni}` : 'Buono pasto',
                        importo: totaleBp,
                        importoApprovato: totaleBp,
                        anticipatoAzienda: false,
                        buonoPastoAuto: true,
                    }]
                    : speseSenzaAuto;
                return { ...r, stato: 'APPROVATO', spese: speseNuove };
            }));
            onApproved?.();
        } catch { addToast('Errore salvataggio', 'error'); }
        finally { setSavingId(null); }
    };

    // Approvazione massiva lato server con i valori inviati dal collaboratore;
    // le rettifiche inline non salvate si perdono (il modale lo segnala).
    const approveBulk = async (lista: any[]) => {
        setSavingBulk(true);
        try {
            const ids = lista.map(r => r.id);
            const res = await RelazioneGiornalieraService.bulkApprove(ids);
            const approvati = new Set(res.approvati || []);
            setReports(prev => prev.map(x => approvati.has(x.id) ? { ...x, stato: 'APPROVATO' } : x));
            setSelectedIds(prev => {
                const s = new Set(prev);
                approvati.forEach(id => s.delete(id));
                return s;
            });
            const okCount = approvati.size;
            const skipCount = (res.saltati || []).length;
            addToast(
                skipCount > 0
                    ? `Approvati ${okCount} report (${skipCount} saltati)`
                    : `Approvati ${okCount} report`,
                'success',
            );
            onApproved?.();
        } catch {
            addToast('Errore approvazione in blocco', 'error');
        } finally {
            setSavingBulk(false);
            setBulkApproveOpen(false);
        }
    };

    const toggleSelected = (id: number) =>
        setSelectedIds(prev => {
            const s = new Set(prev);
            s.has(id) ? s.delete(id) : s.add(id);
            return s;
        });

    const annulla = async (report: any) => {
        if (!window.confirm('Annullare l\'approvazione? Il report tornerà in stato "Da approvare".')) return;
        setAnnullandoId(report.id);
        try {
            await RelazioneGiornalieraService.annullaApprovazione(report.id);
            addToast('Approvazione annullata', 'success');
            // L'annullamento lato BE elimina le righe buono pasto automatiche:
            // si fa lo stesso in locale per non mostrarne una non più valida.
            setReports(prev => prev.map(r => r.id === report.id ? {
                ...r,
                stato: 'DA_REVISIONARE',
                spese: (r.spese || []).filter((s: any) => !s.buonoPastoAuto),
            } : r));
            onApproved?.();
        } catch { addToast('Errore annullamento', 'error'); }
        finally { setAnnullandoId(null); }
    };

    const preview = async (m: any) => {
        setPreviewFile(m);
        setPreviewUrl(m.urlCompressed || m.finalUrl || null);
        try { const u = await IncaricoService.getMediaUrl(m.id); if (u) setPreviewUrl(u); } catch {}
    };

    if (loading) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" size={24}/></div>;
    if (!reports.length) return (
        <div className="py-12 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <FileText size={32} className="text-slate-300 mx-auto mb-2"/>
            <p className="font-bold text-slate-500 text-sm">Nessun report ricevuto</p>
        </div>
    );

    const reportsFiltrati = reports.filter((r: any) => passesApprovazioneFilters(filters, {
        ritardoMin: ritardoMinutiReportIncarico(r, r.incaricoDataInizio),
        km: Number(r.kmApprovati ?? r.kmPercorsi ?? 0),
        spese: totaleSpeseReport(r.spese),
    }));
    const pendingVisibili = reportsFiltrati.filter((r: any) => !r.stato || r.stato === 'DA_REVISIONARE');

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between mb-1">
                <div className="min-w-0">
                    <h2 className="text-sm font-bold text-slate-700">Report dal Campo</h2>
                    {/* Il nostro riferimento, in grande: dice subito di che lavoro si tratta. */}
                    {riferimento?.trim() && (
                        <p className="text-lg font-black text-amber-700 leading-tight break-words" title="Nostro riferimento">{riferimento}</p>
                    )}
                </div>
                <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded flex items-center gap-1">
                    <AlertTriangle size={10} className="text-amber-400"/>Solo approvati → fatturazione
                </span>
            </div>

            <ApprovazioniFilters
                value={filters}
                onChange={setFilters}
                matchedCount={reportsFiltrati.length}
                totalCount={reports.length}
            />

            {pendingVisibili.length > 0 && (() => {
                const selectedCount = pendingVisibili.reduce((n, r) => n + (selectedIds.has(r.id) ? 1 : 0), 0);
                const allSelected = selectedCount === pendingVisibili.length;
                const someSelected = selectedCount > 0 && !allSelected;
                const toggleAll = () => setSelectedIds(prev => {
                    const s = new Set(prev);
                    if (allSelected) {
                        pendingVisibili.forEach(r => s.delete(r.id));
                    } else {
                        pendingVisibili.forEach(r => s.add(r.id));
                    }
                    return s;
                });
                const approveSelected = async () => {
                    const lista = pendingVisibili.filter(r => selectedIds.has(r.id));
                    if (lista.length === 0) return;
                    if (!window.confirm(`Approvare ${lista.length} report selezionati?`)) return;
                    await approveBulk(lista);
                };
                return (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        <label
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 cursor-pointer select-none"
                            title="Seleziona / deseleziona tutti i report pending visibili"
                        >
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={el => { if (el) el.indeterminate = someSelected; }}
                                onChange={toggleAll}
                                className="w-4 h-4 accent-emerald-600 cursor-pointer"
                            />
                            Seleziona tutti
                        </label>
                        {selectedCount > 0 && (
                            <button
                                onClick={approveSelected}
                                disabled={savingBulk}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm disabled:bg-emerald-300 transition-colors"
                            >
                                <CheckCircle2 size={14}/> Approva selezionati ({selectedCount})
                            </button>
                        )}
                        <button
                            onClick={() => setBulkApproveOpen(true)}
                            disabled={savingBulk}
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50 rounded-lg shadow-sm disabled:opacity-50 transition-colors"
                        >
                            <CheckCircle2 size={14}/> Approva tutti ({pendingVisibili.length})
                        </button>
                    </div>
                );
            })()}

            {reportsFiltrati.map(report => {
                const isExp = expanded === report.id;
                const pend = !report.stato || report.stato === 'DA_REVISIONARE';
                const kmPhotos = dedupKmPhotos(kmPhotosMap[report.incaricoId] || []);
                const oreCliente = calcolaOre(report.orarioInizioIndagine, report.orarioFineIndagine);

                return (
                    <div key={report.id} className={`bg-white border rounded-xl overflow-hidden shadow-sm ${pend ? 'border-amber-300' : 'border-emerald-200'}`}>

                        {/* HEADER */}
                        <div className={`px-3 py-2.5 flex items-center justify-between cursor-pointer hover:bg-slate-50 ${pend ? 'bg-amber-50/30' : 'bg-emerald-50/20'}`}
                             onClick={() => setExpanded(isExp ? null : report.id)}>
                            <div className="flex items-center gap-2.5">
                                {pend && (
                                    <label
                                        onClick={e => e.stopPropagation()}
                                        className="p-1 cursor-pointer"
                                        title="Seleziona per approvazione massiva"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(report.id)}
                                            onChange={() => toggleSelected(report.id)}
                                            className="w-4 h-4 accent-emerald-600 cursor-pointer"
                                        />
                                    </label>
                                )}
                                <div className={`p-1.5 rounded-lg ${pend ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                    {pend ? <Clock size={14}/> : <CheckCircle2 size={14}/>}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <p className="font-bold text-slate-800 text-xs leading-tight">
                                            {report.dataRelazione ? new Date(report.dataRelazione).toLocaleDateString('it-IT') : 'N/D'}
                                            <span className="font-normal text-slate-400 ml-1.5">{report.collaboratore?.name} {report.collaboratore?.surname}</span>
                                        </p>

                                        {/* Badge del soggetto */}
                                        <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider border border-orange-100">
                                            <Target size={8}/> {report.soggettoNome}
                                        </span>
                                    </div>
                                    {/* Riepilogo: ore GPS · ore cliente · ore extra · km (aziendale/privata) */}
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-700 px-1.5 py-0.5 rounded-md text-[11px] font-black">
                                            <MapPin size={11}/> Ore GPS {report.oreGps || 0}h
                                        </span>
                                        <span className="inline-flex items-center gap-1 bg-sky-50 border border-sky-200 text-sky-700 px-1.5 py-0.5 rounded-md text-[11px] font-black">
                                            <Clock size={11}/> Ore indagine {Number(oreCliente.toFixed(2))}h
                                        </span>
                                        {/* Valori rettificati dal titolare, validi per la fatturazione */}
                                        <span className="inline-flex items-center gap-1 bg-green-100 border border-green-300 text-green-800 px-1.5 py-0.5 rounded-md text-[11px] font-black">
                                            <Clock size={11}/> Da fatturare {Number(Number(report.oreFatturabiliApprovate ?? oreCliente).toFixed(2))}h
                                        </span>
                                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-black border ${
                                            report.autoAziendale
                                                ? 'bg-purple-50 border-purple-200 text-purple-700'
                                                : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                        }`}>
                                            <Car size={11}/> {report.autoAziendale ? 'Km aziend.' : 'Km priv.'} {report.kmPercorsi || 0}
                                        </span>
                                        <span className="inline-flex items-center gap-1 bg-green-100 border border-green-300 text-green-800 px-1.5 py-0.5 rounded-md text-[11px] font-black">
                                            <Car size={11}/> Km approvati {Number(report.kmApprovati ?? report.kmPercorsi ?? 0)}
                                        </span>
                                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-black border ${
                                            Number(report.oreExtra) > 0
                                                ? 'bg-amber-50 border-amber-200 text-amber-700'
                                                : 'bg-slate-50 border-slate-200 text-slate-400'
                                        }`}>
                                            Extra {report.oreExtra || 0}h
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase rounded ${pend ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                    {pend ? 'Da approvare' : 'Approvato'}
                                </span>
                                {isExp ? <ChevronUp size={13} className="text-slate-400"/> : <ChevronDown size={13} className="text-slate-400"/>}
                            </div>
                        </div>

                        {isExp && (
                            <div className="p-3 border-t border-slate-100 space-y-4 bg-slate-50/20">

                                {/* RELAZIONE + GPS */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                    <div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1"><FileText size={10}/> Relazione</p>
                                        <div className="bg-white border border-slate-200 p-2.5 rounded-lg text-xs text-slate-700 leading-relaxed min-h-[80px] whitespace-pre-wrap shadow-sm">
                                            {report.testoRelazione || <span className="italic text-slate-400">Vuota.</span>}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1"><MapPin size={10}/> GPS</p>
                                        <div className="bg-white border border-slate-200 rounded-lg p-2.5 min-h-[80px] max-h-[160px] overflow-y-auto space-y-1.5 shadow-sm">
                                            {(report.sessioniSelezionate || []).map((s: any, i: number) => (
                                                <div key={i} className="bg-blue-50 border border-blue-100 p-1.5 rounded text-[10px] flex items-center justify-between gap-2">
                                                    <span className="font-bold text-blue-600">S{i+1}</span>
                                                    <span className="text-slate-600">▶ {s.startTime ? new Date(s.startTime).toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit'}) : '—'}</span>
                                                    <span className="text-slate-600">■ {s.endTime ? new Date(s.endTime).toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit'}) : '…'}</span>
                                                    {s.startLat && (
                                                        <a href={`https://maps.google.com/?q=${s.startLat},${s.startLon}`} target="_blank" rel="noreferrer" className="text-emerald-500 hover:text-emerald-700 flex items-center gap-0.5" title="Posizione inizio">
                                                            <MapPin size={10}/>▶
                                                        </a>
                                                    )}
                                                    {s.endLat && (
                                                        <a href={`https://maps.google.com/?q=${s.endLat},${s.endLon}`} target="_blank" rel="noreferrer" className="text-rose-400 hover:text-rose-600 flex items-center gap-0.5" title="Posizione fine">
                                                            <MapPin size={10}/>■
                                                        </a>
                                                    )}
                                                </div>
                                            ))}
                                            {(report.noteSelezionate || []).map((n: any, i: number) => (
                                                <div key={i} className="bg-amber-50 border border-amber-100 p-1.5 rounded text-[10px]">
                                                    <span className="font-bold text-amber-600">{n.createdAt ? new Date(n.createdAt).toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit'}) : ''}</span>
                                                    <span className="text-slate-600 ml-1 italic">"{n.text}"</span>
                                                </div>
                                            ))}
                                            {!report.sessioniSelezionate?.length && !report.noteSelezionate?.length && <p className="text-[9px] text-slate-400 text-center py-3">Nessun evento.</p>}
                                        </div>
                                    </div>
                                </div>

                                {/* Sezione prove disattivata: le foto si vedono dal tab Media. */}
                                {/*
                                {report.mediaSelezionati?.length > 0 && (
                                    <div>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-1"><Camera size={10}/> Prove</p>
                                        <div className="flex gap-2 overflow-x-auto pb-1">
                                            {report.mediaSelezionati.map((m: any) => (
                                                <div key={m.id} className="flex-shrink-0 cursor-pointer" onClick={() => preview(m)}>
                                                    {m.fileType?.includes('video')
                                                        ? <div className="w-20 h-14 bg-purple-100 rounded border flex items-center justify-center"><Video size={16} className="text-purple-500"/></div>
                                                        : <img src={m.urlCompressed || m.finalUrl} className="w-20 h-14 object-cover rounded border border-slate-200 hover:border-blue-400" alt=""/>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                */}

                                {/* REVISIONE CONTABILE */}
                                <div className="border-t border-slate-200 pt-3">
                                    <p className="text-[9px] font-bold text-slate-600 uppercase tracking-widest mb-2.5 flex items-center gap-1"><Clock size={11} className="text-blue-400"/> Revisione</p>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">

                                        {/* Ore: totale approvato (GPS + extra) in un'unica casella */}
                                        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                                            <p className="text-[9px] font-bold text-slate-400 uppercase mb-2">Ore</p>
                                            <div className="space-y-1 mb-2">
                                                {[['Registrate', report.oreGps || 0, 'h'], ['Extra chieste', report.oreExtra || 0, 'h']].map(([l, v, u]) => (
                                                    <div key={String(l)} className="flex justify-between text-[10px]">
                                                        <span className="text-slate-400">{l}:</span>
                                                        <span className="font-black text-slate-700">{v}{u}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            {(() => {
                                                const edited = report.oreApprovateTotali !== undefined && report.oreApprovateTotali !== null;
                                                const defaultPending = (Number(report.oreGps) || 0) + (Number(report.oreExtra) || 0);
                                                const defaultApprovato = (Number(report.oreGps) || 0) + (Number(report.oreExtraApprovate) || 0);
                                                return pend ? (
                                                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-2">
                                                        <p className="text-[8px] font-bold text-blue-700 uppercase mb-1">Ore approvate</p>
                                                        <div className="flex items-center gap-1">
                                                            {edited && (
                                                                <button title="Ripristina default GPS + extra" onClick={() => setField(report.id,'oreApprovateTotali',null)} className="p-1 bg-white text-rose-400 border border-rose-200 rounded hover:bg-rose-50"><XCircle size={12}/></button>
                                                            )}
                                                            <NumberInput
                                                                   min={0} step="0.5" className="flex-1 p-1 text-center font-black text-xs bg-white border border-blue-200 rounded outline-none focus:ring-1 ring-blue-400"
                                                                   value={edited ? report.oreApprovateTotali : defaultPending}
                                                                   onFocus={e => e.currentTarget.select()}
                                                                   onChange={e => setField(report.id,'oreApprovateTotali', e.target.value===''?'':Number(e.target.value))}/>
                                                            <span className="text-[10px] font-bold text-blue-500">h</span>
                                                        </div>
                                                        <p className="text-[8px] text-slate-400 mt-1">
                                                            Default: {Number(report.oreGps) || 0}h{(Number(report.oreExtra) || 0) > 0 ? ` + ${Number(report.oreExtra)}h extra` : ''}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="bg-emerald-50 border border-emerald-100 rounded p-1.5 flex justify-between items-center">
                                                        <span className="text-[8px] font-bold text-emerald-600 uppercase">Approvate:</span>
                                                        <span className="font-black text-emerald-700 text-xs">{edited ? report.oreApprovateTotali : defaultApprovato}h</span>
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        {/* ORE CLIENTE */}
                                        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                                            <p className="text-[9px] font-bold text-slate-400 uppercase mb-2 flex items-center gap-1"><Users size={10}/> Ore Cliente</p>
                                            <div className="space-y-1 mb-2">
                                                {[['Inizio', report.orarioInizioIndagine||'—'], ['Fine', report.orarioFineIndagine||'—'], ['Calcolate', `${Number(oreCliente.toFixed(2))}h`]].map(([l,v]) => (
                                                    <div key={String(l)} className="flex justify-between text-[10px]">
                                                        <span className="text-slate-400">{l}:</span>
                                                        <span className={`font-black ${l==='Calcolate' ? 'text-blue-600' : 'text-slate-700'}`}>{v}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            {pend ? (
                                                <div className="bg-blue-50 border border-blue-100 rounded-lg p-2">
                                                    <p className="text-[8px] font-bold text-blue-700 uppercase mb-1">Ore da fatturare</p>
                                                    <div className="flex items-center gap-1">
                                                        <button onClick={() => setField(report.id,'oreFatturabiliApprovate',oreCliente)} className="px-1.5 py-1 bg-white text-slate-400 border border-slate-200 rounded text-[8px] font-bold hover:bg-slate-50">↺</button>
                                                        <NumberInput
                                                               min={0} step="0.5" className="flex-1 p-1 text-center font-black text-xs bg-white border border-blue-200 rounded outline-none focus:ring-1 ring-blue-400"
                                                               value={report.oreFatturabiliApprovate} onChange={e => setField(report.id,'oreFatturabiliApprovate', e.target.value===''?'':Number(e.target.value))}/>
                                                        <span className="text-[10px] font-bold text-blue-500">h</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="bg-emerald-50 border border-emerald-100 rounded p-1.5 flex justify-between items-center">
                                                    <span className="text-[8px] font-bold text-emerald-600 uppercase">Da Fatturare:</span>
                                                    <span className="font-black text-emerald-700 text-xs">{report.oreFatturabiliApprovate}h</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* KM */}
                                        <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm flex flex-col">
                                            <p className="text-[9px] font-bold text-slate-400 uppercase mb-2 flex items-center gap-1"><Car size={10}/> Km</p>
                                            <div className="flex justify-between text-[10px] mb-1.5">
                                                <span className="text-slate-400">Dichiarati:</span>
                                                <span className="font-black text-slate-700">{report.kmPercorsi || 0} km</span>
                                            </div>
                                            <AutoAziendaleSelector
                                                size="xs"
                                                className="mb-2"
                                                value={!!report.autoAziendale}
                                                onChange={async nuovoVal => {
                                                    setField(report.id, 'autoAziendale', nuovoVal);
                                                    try {
                                                        await RelazioneGiornalieraService.updateAutoAziendale(report.id, nuovoVal);
                                                    } catch {
                                                        setField(report.id, 'autoAziendale', !nuovoVal);
                                                        addToast('Errore aggiornamento auto', 'error');
                                                    }
                                                }}
                                            />
                                            {kmPhotos.length > 0 ? (
                                                <div className="flex gap-1.5 mb-2">
                                                    {kmPhotos.map((m: any) => (
                                                        <div key={m.id} className="relative cursor-pointer flex-shrink-0" onClick={() => preview(m)}>
                                                            <img src={m.urlCompressed || m.finalUrl} alt="km"
                                                                 className="w-16 h-12 object-cover rounded border border-slate-200 hover:border-blue-400"
                                                                 onError={e => { (e.target as HTMLImageElement).style.display='none'; }}/>
                                                            <div className={`absolute inset-x-0 bottom-0 text-white text-[7px] font-bold text-center py-0.5 rounded-b ${m.filename?.includes('Iniziali') ? 'bg-emerald-600/90' : 'bg-rose-600/90'}`}>
                                                                {m.filename?.includes('Iniziali') ? '▶' : '■'}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-[8px] text-slate-400 italic mb-2">Nessuna foto.</p>
                                            )}
                                            <div className="mt-auto">
                                                {pend ? (
                                                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-2">
                                                        <p className="text-[8px] font-bold text-blue-700 uppercase mb-1">Km approvati</p>
                                                        <div className="flex items-center gap-1">
                                                            <button onClick={() => setField(report.id,'kmApprovati',0)} className="p-1 bg-white text-rose-400 border border-rose-200 rounded hover:bg-rose-50"><XCircle size={12}/></button>
                                                            <NumberInput
                                                                   min={0} className="flex-1 p-1 text-center font-black text-xs bg-white border border-blue-200 rounded outline-none focus:ring-1 ring-blue-400"
                                                                   value={report.kmApprovati} onChange={e => setField(report.id,'kmApprovati', e.target.value===''?'':Number(e.target.value))}/>
                                                            <span className="text-[10px] font-bold text-blue-500">km</span>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="bg-emerald-50 border border-emerald-100 rounded p-1.5 flex justify-between items-center">
                                                        <span className="text-[8px] font-bold text-emerald-600 uppercase">Approvati:</span>
                                                        <span className="font-black text-emerald-700 text-xs">{report.kmApprovati} km</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Spese: in pending le righe buono pasto automatiche sono nascoste,
                                    le gestisce BuonoPastoAutoCard. */}
                                {(() => {
                                    const speseVisibili = (report.spese || []).filter((s: any) => !(pend && s.buonoPastoAuto));
                                    return speseVisibili.length > 0 && (
                                    <div className="border-t border-slate-200 pt-3">
                                        <p className="text-[9px] font-bold text-slate-600 uppercase tracking-widest mb-2 flex items-center gap-1.5"><Receipt size={11} className="text-blue-400"/> Spese</p>
                                        <div className="space-y-1.5">
                                            {speseVisibili.map((s: any) => (
                                                <div key={s.id} className={`flex items-center gap-2 p-2 rounded-lg border text-[10px] ${s.anticipatoAzienda ? 'bg-purple-50/50 border-purple-200' : 'bg-white border-slate-200'}`}>
                                                    <div className={`p-1 rounded ${s.anticipatoAzienda ? 'bg-purple-100 text-purple-600' : 'bg-slate-100 text-slate-400'}`}>
                                                        {s.anticipatoAzienda ? <CreditCard size={11}/> : <Receipt size={11}/>}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <span className="font-bold text-slate-700">{s.tipo}</span>
                                                            {/* In pending è un toggle, dopo l'approvazione un badge */}
                                                            {pend && canRettificareSpese ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleSpesaAnticipata(report.id, s.id)}
                                                                    className={`px-1 py-0.5 text-[8px] font-black uppercase rounded-full border transition-colors ${
                                                                        s.anticipatoAzienda
                                                                            ? 'bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200'
                                                                            : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                                                                    }`}
                                                                    title={s.anticipatoAzienda
                                                                        ? 'Click per cambiare in "pagata di tasca"'
                                                                        : 'Click per cambiare in "carta aziendale"'}
                                                                >
                                                                    {s.anticipatoAzienda ? '💳 Carta az.' : '💰 Tasca'}
                                                                </button>
                                                            ) : s.anticipatoAzienda && (
                                                                <span className="px-1 py-0.5 bg-purple-100 text-purple-700 text-[8px] font-black uppercase rounded-full border border-purple-200">💳 Carta az.</span>
                                                            )}
                                                            <span className={`${!s.anticipatoAzienda && Number(s.importoApprovato) !== Number(s.importo) ? 'line-through text-rose-400' : 'text-slate-400'}`}>€{Number(s.importo||0).toFixed(2)}</span>
                                                        </div>
                                                    </div>
                                                    {s.scontrinoUrl && (
                                                        <button type="button"
                                                                onClick={() => {
                                                                    // Tipo dedotto dall'estensione (senza query string); default immagine.
                                                                    const clean = String(s.scontrinoUrl).split('?')[0].toLowerCase();
                                                                    const isPdf = clean.endsWith('.pdf');
                                                                    const isVideo = /\.(mp4|mov|webm|m4v|mts|m2ts|mkv)$/.test(clean);
                                                                    setPreviewFile({
                                                                        filename: `Scontrino${s.tipo ? ` - ${s.tipo}` : ''}`,
                                                                        fileType: isPdf ? 'application/pdf' : (isVideo ? 'video/mp4' : 'image/jpeg'),
                                                                    });
                                                                    setPreviewUrl(s.scontrinoUrl);
                                                                }}
                                                                title="Apri scontrino"
                                                                className="text-blue-400 hover:text-blue-600 shrink-0">
                                                            <ExternalLink size={11}/>
                                                        </button>
                                                    )}
                                                    {s.anticipatoAzienda && pend ? (
                                                            <div className="flex items-center gap-1 shrink-0" title="Importo pagato con carta aziendale">
                                                                <span className="text-[8px] font-black text-purple-600 uppercase">Carta €</span>
                                                                <NumberInput min={0} step="0.01"
                                                                    className="w-16 p-1 text-center font-black text-[10px] bg-white border border-purple-200 rounded outline-none focus:ring-1 ring-purple-400"
                                                                    value={s.importoCarta ?? s.importo}
                                                                    onChange={e => setSpesa(report.id, s.id, e.target.value === '' ? '' : Number(e.target.value), 'importoCarta')}/>
                                                            </div>
                                                    ) : s.anticipatoAzienda ? (
                                                        <span className="text-[8px] font-black text-purple-600 uppercase shrink-0 whitespace-nowrap">No rimborso</span>
                                                    ) : pend ? (
                                                        <div className="flex items-center gap-1 shrink-0">
                                                            <span className="font-bold text-blue-600 text-[9px]">€</span>
                                                            <NumberInput
                                                                   min={0} step="0.01" className="w-16 p-1 text-center font-black text-[10px] bg-white border border-blue-200 rounded outline-none focus:ring-1 ring-blue-400"
                                                                   value={s.importoApprovato} onChange={e => setSpesa(report.id, s.id, e.target.value===''?'':Number(e.target.value))}/>
                                                            <button onClick={() => setSpesa(report.id, s.id, s.importo)} className="p-0.5 text-rose-400 border border-rose-200 rounded bg-white hover:bg-rose-50"><XCircle size={11}/></button>
                                                        </div>
                                                    ) : (
                                                        <span className="font-black text-emerald-700 text-xs shrink-0">€{Number(s.importoApprovato||0).toFixed(2)}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    );
                                })()}

                                {/* Buono pasto automatico, se configurato per il collaboratore.
                                    Si basa sulle ore approvate al collaboratore, non su quelle
                                    fatturate al cliente, come nel BE. */}
                                {pend && (
                                    <BuonoPastoAutoCard
                                        oreLavorate={
                                            report.oreApprovateTotali !== null
                                            && report.oreApprovateTotali !== undefined
                                            && report.oreApprovateTotali !== ''
                                                ? Number(report.oreApprovateTotali) || 0
                                                : (Number(report.oreGps) || 0) + (Number(report.oreExtraApprovate) || 0)
                                        }
                                        sogliaOre={report.collaboratore?.sogliaBuonoPastoIncarichiOre}
                                        importo={report.collaboratore?.importoBuonoPastoIncarichi}
                                        applica={isBpAttivo(report.id)}
                                        onChangeApplica={v => setApplicaBpByRel(prev => ({ ...prev, [report.id]: v }))}
                                    />
                                )}

                                {/* APPROVA / ANNULLA */}
                                <div className="flex justify-end pt-2.5 border-t border-slate-200">
                                    {pend ? (
                                        <button onClick={() => save(report)} disabled={savingId === report.id}
                                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-1.5 text-xs shadow-md transition-all active:scale-[0.98] disabled:opacity-50">
                                            {savingId === report.id ? <Loader2 size={13} className="animate-spin"/> : <CheckCircle2 size={13}/>}
                                            Approva e Invia a Contabilità
                                        </button>
                                    ) : (
                                        <button onClick={() => annulla(report)} disabled={annullandoId === report.id}
                                                className="bg-white hover:bg-rose-50 text-rose-600 border border-rose-200 px-3 py-1.5 rounded-lg font-bold flex items-center gap-1.5 text-xs transition-all active:scale-[0.98] disabled:opacity-50">
                                            {annullandoId === report.id ? <Loader2 size={12} className="animate-spin"/> : <XCircle size={12}/>}
                                            Annulla approvazione
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {previewFile && (
                <Modal title={previewFile.filename || 'Anteprima'} onClose={() => { setPreviewFile(null); setPreviewUrl(null); }}>
                    <div className="flex flex-col items-center bg-slate-900 -mx-5 -mb-5 p-4 rounded-b-xl">
                        {previewFile.fileType?.includes('pdf') ? (
                            <iframe src={previewUrl || ''} title={previewFile.filename || 'PDF'} className="w-full h-[55vh] rounded-lg bg-white"/>
                        ) : previewFile.fileType?.includes('video') ? (
                            <video src={previewUrl||previewFile.finalUrl} controls className="w-full rounded-lg max-h-[55vh] bg-black"/>
                        ) : (
                            <img src={previewUrl||previewFile.urlCompressed||previewFile.finalUrl} className="w-full rounded-lg max-h-[55vh] object-contain" alt=""/>
                        )}
                        <p className="text-[9px] text-slate-400 mt-2 truncate w-full text-center">{previewFile.filename}</p>
                    </div>
                </Modal>
            )}

            {bulkApproveOpen && (
                <BulkApproveModal
                    count={pendingVisibili.length}
                    filters={filters}
                    saving={savingBulk}
                    label="report"
                    onCancel={() => { if (!savingBulk) setBulkApproveOpen(false); }}
                    onConfirm={() => approveBulk(pendingVisibili)}
                />
            )}
        </div>
    );
};